import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Plus, Lock, Sparkles } from 'lucide-react';
import { canCreateHabit } from '@/lib/tier-utils';
import { UpgradeModal } from '@/components/subscription/UpgradeModal';
import { trackLimitHit, trackHabitCreated } from '@/lib/analytics';
import { BETA_CONFIG } from '@/config/beta';

interface Goal {
  id: string;
  title: string;
}

interface AddHabitToActionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  actionText: string;
  milestoneIndex: number;
  actionIndex: number;
  planId: string;
  onHabitAdded: () => void;
}

export const AddHabitToActionDialog = ({
  isOpen,
  onClose,
  actionText,
  milestoneIndex,
  actionIndex,
  planId,
  onHabitAdded,
}: AddHabitToActionDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [habitTitle, setHabitTitle] = useState('');
  const [frequency, setFrequency] = useState<'daily' | 'weekly'>('daily');
  const [selectedGoalId, setSelectedGoalId] = useState<string>('');
  const [goals, setGoals] = useState<Goal[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAtLimit, setIsAtLimit] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  // Check habit limit
  useEffect(() => {
    const checkLimit = async () => {
      if (!user || !isOpen) return;
      const result = await canCreateHabit(user.id);
      setIsAtLimit(!BETA_CONFIG.allFeaturesUnlocked && !result.allowed);
    };
    checkLimit();
  }, [user, isOpen]);

  // Fetch user's goals
  useEffect(() => {
    const fetchGoals = async () => {
      if (!user || !isOpen) return;
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('goals')
        .select('id, title')
        .eq('user_id', user.id);

      if (!error && data) {
        setGoals(data);
        if (data.length > 0 && !selectedGoalId) {
          setSelectedGoalId(data[0].id);
        }
      }
      setIsLoading(false);
    };

    fetchGoals();
  }, [user, isOpen]);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setHabitTitle('');
      setFrequency('daily');
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !habitTitle.trim() || !selectedGoalId) return;

    // Re-check limit before submitting
    const limitResult = await canCreateHabit(user.id);
    if (!BETA_CONFIG.allFeaturesUnlocked && !limitResult.allowed) {
      trackLimitHit('habit', { currentCount: limitResult.currentCount, limit: limitResult.limit });
      setUpgradeModalOpen(true);
      return;
    }

    setIsSaving(true);

    try {
      // First, ensure the milestone_action record exists
      let actionId: string;
      
      const { data: existingAction, error: existingError } = await supabase
        .from('milestone_actions')
        .select('id')
        .eq('plan_id', planId)
        .eq('milestone_index', milestoneIndex)
        .eq('action_index', actionIndex)
        .maybeSingle();

      if (existingError) throw existingError;

      if (existingAction) {
        actionId = existingAction.id;
      } else {
        // Create the milestone_action record
        const { data: newAction, error: insertError } = await supabase
          .from('milestone_actions')
          .insert({
            user_id: user.id,
            plan_id: planId,
            milestone_index: milestoneIndex,
            action_index: actionIndex,
            completed: false,
          })
          .select('id')
          .single();

        if (insertError) throw insertError;
        actionId = newAction.id;
      }

      // Create the habit linked to this action
      const { error: habitError } = await supabase
        .from('habits')
        .insert({
          goal_id: selectedGoalId,
          title: habitTitle.trim(),
          frequency,
          schedule_days: frequency === 'daily' ? 127 : null,
          linked_action_id: actionId,
        });

      if (habitError) throw habitError;

      // Track habit creation (fire-and-forget)
      supabase.from('profiles').select('tier').eq('user_id', user.id).single().then(({ data: profile }) => {
        trackHabitCreated(profile?.tier || 'free', goals.length);
      });

      toast({
        title: 'Habit created!',
        description: `"${habitTitle}" is now linked to your development plan.`,
      });

      onHabitAdded();
      onClose();
    } catch (error) {
      console.error('Error creating habit:', error);
      toast({
        title: 'Error creating habit',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              Add Habit to Action
            </DialogTitle>
            <DialogDescription className="line-clamp-2 text-sm">
              Create a habit that will track progress for: {actionText}
            </DialogDescription>
          </DialogHeader>

          {isAtLimit ? (
            <div className="py-6 text-center space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-muted flex items-center justify-center">
                <Lock className="w-8 h-8 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Habit Limit Reached</h3>
                <p className="text-muted-foreground text-sm mt-1">
                  You've reached your 5-habit limit. Upgrade to Pro for unlimited habits.
                </p>
              </div>
              <Button
                onClick={() => setUpgradeModalOpen(true)}
                className="bg-gradient-to-r from-primary to-accent"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Upgrade to Pro
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          {/* Habit Title */}
          <div className="space-y-2">
            <Label htmlFor="habit-title">Habit Name</Label>
            <Input
              id="habit-title"
              value={habitTitle}
              onChange={(e) => setHabitTitle(e.target.value)}
              placeholder="e.g., Practice public speaking for 15 minutes"
              className="h-12"
              autoFocus
            />
          </div>

          {/* Frequency */}
          <div className="space-y-2">
            <Label htmlFor="frequency">Frequency</Label>
            <Select value={frequency} onValueChange={(v) => setFrequency(v as 'daily' | 'weekly')}>
              <SelectTrigger id="frequency" className="h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Goal Selection */}
          <div className="space-y-2">
            <Label htmlFor="goal">Associated Goal</Label>
            {isLoading ? (
              <div className="h-12 flex items-center justify-center">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            ) : goals.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Create a goal first to add habits.
              </p>
            ) : (
              <Select value={selectedGoalId} onValueChange={setSelectedGoalId}>
                <SelectTrigger id="goal" className="h-12">
                  <SelectValue placeholder="Select a goal" />
                </SelectTrigger>
                <SelectContent>
                  {goals.map((goal) => (
                    <SelectItem key={goal.id} value={goal.id}>
                      {goal.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={!habitTitle.trim() || !selectedGoalId || isSaving}
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Add Habit
              </Button>
            </div>
          </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        trigger="habit_limit"
      />
    </>
  );
};
